<template>
  <div :id="id + '_acc'" :class="_box_class" :data-label="title">
    <div class="edit-panel clearfix">
      <component :is="headingComponent">
        <a
          :id="id + '_acc_heading'"
          role="button"
          tabindex="0"
          class="cat-accordion-trigger"
          :aria-controls="id + '_acc_panel'"
          :aria-expanded="open ? 'true' : 'false'"
          :aria-disabled="open"
          data-label-type="accordion-section-title"
          :data-label="title"
          :aria-describedby="offscreenDesc ? id + '-acc-offscreen-desc' : null"
          @click="selectBox"
          @keyup.enter.self.prevent="selectBox"
          @keydown.space.self.prevent="selectBox"
          @keydown.down.self.prevent="focusNextBox"
          @keydown.up.self.prevent="focusPreviousBox"
          ><cat-icon
            :icon="open ? 'angle-down' : 'angle-right'"
            decorative
            class="mrn cat-trigger"
          ></cat-icon
          >{{ title
          }}<span
            v-if="offscreenDesc"
            :id="id + '-acc-offscreen-desc'"
            style="display: none"
            >{{ offscreenDesc }}</span
          >
          <cat-icon
            v-if="completed && visited && $parent.progress"
            icon="ok"
            :icon-label="
              resolveTxt('skin.default.section.completed', 'Section completed')
            "
            class="mln mrn progress-icon"
          ></cat-icon>
        </a>
        <cat-help-trigger
          v-if="help && help.url"
          :sourceId="id"
          :url="help.url"
          :title="title"
        />
        <div
          v-if="!completed && !(revisit && open)"
          class="errors cat-accordion-errors"
          aria-hidden="true"
        >
          <cat-icon icon="attention" />
          {{ resolveTxt("skin.error.incomplete", "incomplete") }}
        </div>
      </component>
      <transition-expand duration="0.3s" @ui-expand-enter="scrollIntoView">
        <div
          v-if="open"
          :id="id + '_acc_panel'"
          class="fields"
          role="region"
          :aria-labelledby="id + '_acc_heading'"
        >
          <div style="padding-top: 0px">
            <cat-hint :id="id + '_hint'" :text="hint" />
            <slot></slot>
          </div>
        </div>
      </transition-expand>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import jQuery from "jquery";
import CatHint from "@/components/utilities/hint.vue";
import CatIcon from "@/components/utilities/catIcon";
import TransitionExpand from "@/components/utilities/catTransitionExpand";
import { defaultStopProcessUpdate } from "@/components/utilities/processUpdateMixin";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatAccordianBox",
  components: {
    TransitionExpand,
    CatIcon,
    CatHelpTrigger,
    CatHint
  },
  props: {
    title: { type: [String], description: "Box title" },
    hint: { type: [String], description: "Box hint" },
    help: { type: Object, description: "Help related information." },
    id: {
      type: [String, Number],
      description: "The node id of accordion panel"
    },
    nextid: {
      type: [String, Number],
      description: "The node id of next accordion panel"
    },
    titleLevel: {
      type: [Number],
      description: "Accordion section title level",
      default: 3
    },
    open: {
      type: [Boolean, String],
      description: "Determines accordion section is initially opened"
    },
    completed: {
      type: [Boolean, String],
      description: "Determines accordion content has been completed"
    },
    visited: {
      type: [Boolean],
      description: "Tracks whether the accordion section has been visited"
    }
  },
  inject: {
    stopProcessUpdate: {
      from: "stopProcessUpdate",
      default: () => defaultStopProcessUpdate
    },
    resolveTxt: { name: "resolveTxt", default: (key, def) => def }
  },
  data: function () {
    return {
      revisit: this.open && this.completed
    };
  },
  methods: {
    selectBox: function (evt) {
      // Do nothing if already open
      if (this.open) {
        this.stopProcessUpdate(
          "accordian-box.vue name: cat-accordian-box method: selectBox"
        );
        return;
      }
      // Set focus to the empty div to screen reader will be able
      // to see that the collapsed accordion (in focus) is expanded

      this.revisit = true;
      this.$emit("ui-select", {
        id: this.id
      });
    },
    focusPreviousBox: function (evt) {
      this.$emit("ui-focus-previous", {
        id: this.id
      });
    },
    focusNextBox: function (evt) {
      this.$emit("ui-focus-next", {
        id: this.id
      });
    },
    scrollIntoView: function (evt) {
      const section = jQuery("#" + this.id + "_acc");
      const heading = jQuery("#" + this.id + "_acc_heading");
      const viewTop = jQuery(window).scrollTop();
      const neededOffset = section.offset().top - jQuery("header").height();

      // Focus to Accordion title
      heading.focus();

      if (neededOffset < viewTop) {
        jQuery("html, body").animate({ scrollTop: neededOffset }, 200);
      }
      this.stopProcessUpdate(
        "accordian-box.vue name: cat-accordian-box method: scrollIntoView"
      );
    }
  },
  computed: {
    _box_class: function () {
      var contextCss = "acc-content-" + this.id;
      var css = {
        error: !this.completed && this.visited,
        maxi: this.open,
        mini: this.visited && !this.open,
        revisited: this.visited && this.completed && this.open,
        completed: this.completed
      };
      css[contextCss] = true;
      return css;
    },
    offscreenDesc: function () {
      var desc = null;
      if (!this.completed) {
        desc = "Form has incomplete entries";
      }
      return desc;
    },
    headingComponent: function () {
      return "h" + (this.titleLevel > 6 ? 6 : this.titleLevel);
    }
  }
});
</script>
